import { useState } from 'react';
import { X, Plus, Trash2, AlertCircle } from 'lucide-react';
import { Task, User, TaskType, TaskStatus, Epic, Story, Project } from '../types';
import { ensureCostItemCode } from '../utils/costItems';
import { RichDescriptionEditor } from './RichDescriptionEditor';
import { APP_NATIVE_FIELD_CLASS } from '../constants/formControls';

interface CreateTaskModalProps {
  projectId: string;
  onClose: () => void;
  onCreate: (task: Task) => void;
  users: User[];
  teamMemberIds?: string[];
  existingTasks: Task[];
  epics: Epic[];
  stories: Story[];
  project?: Project | null;
  orgId: string;
  canCreateTasks?: boolean;
}

interface SelectionOption {
  id: string;
  label: string;
}

export default function CreateTaskModal({
  projectId,
  onClose,
  onCreate,
  users,
  teamMemberIds,
  existingTasks,
  epics,
  stories,
  project,
  orgId,
  canCreateTasks = true,
}: CreateTaskModalProps) {
  // Permission check
  if (!canCreateTasks) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
        <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 text-card-foreground shadow-lg">
          <div className="flex items-start gap-3 mb-4">
            <AlertCircle className="w-6 h-6 text-destructive flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-semibold mb-1">Permission Denied</h2>
              <p className="text-sm text-muted-foreground">You do not have permission to create tasks in this project.</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-full rounded-lg border border-border bg-muted px-4 py-2 text-foreground transition-colors hover:bg-accent"
          >
            Close
          </button>
        </div>
      </div>
    );
  }
  const [formData, setFormData] = useState({
    id: '',  // Optional user-provided id
    title: '',
    description: '',
    type: 'Feature' as TaskType,
    status: 'Active' as TaskStatus,
    priority: 'Medium' as 'Low' | 'Medium' | 'High' | 'Critical',
    estimatedDays: 0,
    epicId: '',
    storyId: '',
    customerVisible: false,
    reportedBy: '',
  });
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [parentTaskIds, setParentTaskIds] = useState<string[]>([]);
  const [costItemIds, setCostItemIds] = useState<string[]>([]);
  const [requirementIds, setRequirementIds] = useState<string[]>([]);
  const [externalLinks, setExternalLinks] = useState<{ type: string; url: string }[]>([]);

  const fieldClassName = `w-full ${APP_NATIVE_FIELD_CLASS}`;
  const checklistContainerClass =
    'max-h-40 overflow-y-auto rounded-lg border border-input bg-background p-3 space-y-1';

  const toggleSelection = (
    selectedIds: string[],
    optionId: string,
    checked: boolean
  ): string[] => {
    if (checked) {
      return selectedIds.includes(optionId) ? selectedIds : [...selectedIds, optionId];
    }

    return selectedIds.filter((id) => id !== optionId);
  };

  const renderSelectionChecklist = (
    label: string,
    options: SelectionOption[],
    selectedIds: string[],
    onToggle: (optionId: string, checked: boolean) => void
  ) => (
    <div>
      <label className="mb-2 block">{label}</label>
      <div className={checklistContainerClass}>
        {options.map((option) => (
          <label key={option.id} className="flex items-center gap-2 py-1 text-sm text-foreground/90">
            <input
              type="checkbox"
              checked={selectedIds.includes(option.id)}
              onChange={(event) => onToggle(option.id, event.target.checked)}
              className="h-4 w-4"
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    </div>
  );

  const normalizeIdentity = (value?: string | null): string => {
    return (value || '').trim().toLowerCase();
  };

  const buildIdentitySet = (value?: string | null): Set<string> => {
    const identities = new Set<string>();
    const normalized = normalizeIdentity(value);

    if (!normalized) {
      return identities;
    }

    identities.add(normalized);

    const withoutCommonPrefix = normalized.replace(/^user[-_]/, '');
    if (withoutCommonPrefix) {
      identities.add(withoutCommonPrefix);
    }

    const compact = normalized.replace(/[^a-z0-9]/g, '');
    if (compact) {
      identities.add(compact);
    }

    if (normalized.includes('@')) {
      const localPart = normalized.split('@')[0];
      if (localPart) {
        identities.add(localPart);
        identities.add(localPart.replace(/[^a-z0-9]/g, ''));
      }
    }

    return identities;
  };

  const teamMemberIdSet = teamMemberIds
    ? new Set(teamMemberIds.map((id) => normalizeIdentity(id)).filter(Boolean))
    : null;

  const assignableUsers = teamMemberIdSet
    ? users.filter((user) => {
        const userIdentities = new Set<string>();
        buildIdentitySet(user.id).forEach((identity) => userIdentities.add(identity));
        buildIdentitySet(user.email).forEach((identity) => userIdentities.add(identity));

        return Array.from(userIdentities).some((identity) => teamMemberIdSet.has(identity));
      })
    : users;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const sanitizedAssigneeIds = assigneeIds.filter((assigneeId) =>
      assignableUsers.some((user) => user.id === assigneeId)
    );
    const projectRequirementIds = new Set((project?.requirements || []).map((req) => req.id));
    const sanitizedRequirementIds = requirementIds.filter((requirementId) =>
      projectRequirementIds.has(requirementId)
    );
    const existingTaskIds = existingTasks.map((task) => task.id);
    const userProvidedId = formData.id?.trim();
    
    // Check if user-provided id already exists
    if (userProvidedId && existingTaskIds.includes(userProvidedId)) {
      alert(`A task with ID "${userProvidedId}" already exists. Please use a different ID or leave blank.`);
      return;
    }
    
    const task: Task = {
      ...(userProvidedId && { id: userProvidedId }),  // Only include id if user provided one
      projectId,
      title: formData.title,
      description: formData.description,
      type: formData.type,
      status: formData.status,
      priority: formData.priority,
      assigneeIds: sanitizedAssigneeIds,
      parentTaskIds,
      subtaskIds: [],
      epicId: formData.epicId || undefined,
      storyId: formData.storyId || undefined,
      estimatedDays: formData.estimatedDays,
      timeLogs: [],
      standupNotes: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      reportedBy: formData.reportedBy || undefined,
      customerVisible: formData.customerVisible,
      attachments: [],
      verificationLinks: [],
      externalLinks,
      costItemIds,
      requirementIds: sanitizedRequirementIds,
      changeLogs: [],
      organizationId: orgId
    } as Task;
    console.log("[CreateTaskModal/handleSubmit]", task);
    onCreate(task);
  };

  const addExternalLink = () => {
    setExternalLinks([...externalLinks, { type: '', url: '' }]);
  };

  const removeExternalLink = (index: number) => {
    setExternalLinks(externalLinks.filter((_, i) => i !== index));
  };

  const updateExternalLink = (index: number, field: 'type' | 'url', value: string) => {
    const newLinks = [...externalLinks];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setExternalLinks(newLinks);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4">
      <div className="my-8 w-full max-w-4xl rounded-lg border border-border bg-card text-card-foreground shadow-xl">
        <div className="sticky top-0 flex items-center justify-between rounded-t-lg border-border bg-card px-6 py-4">
          <h2 className="text-2xl">Create New Task</h2>
          <button
            onClick={onClose}
            aria-label="Close create task dialog"
            title="Close"
            className="rounded-lg p-2 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Task Type */}
            <div>
              <label className="block mb-2">Task Type *</label>
              <select
                aria-label="Task type"
                required
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as TaskType })}
                className={fieldClassName}
              >
                <option value="Feature">Feature</option>
                <option value="Bug">Bug</option>
                <option value="Improvement">Improvement</option>
              </select>
            </div>

            {/* Priority */}
            <div>
              <label className="block mb-2">Priority *</label>
              <select
                aria-label="Task priority"
                required
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                className={fieldClassName}
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block mb-2">Title *</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className={fieldClassName}
              placeholder="Enter task title"
            />
          </div>

          {/* ID (optional) */}
          <div>
            <label className="block mb-2">
              ID <span className="text-xs text-muted-foreground">(optional - leave blank for auto-generated)</span>
            </label>
            <input
              type="text"
              value={formData.id}
              onChange={(e) => setFormData({ ...formData, id: e.target.value })}
              className={fieldClassName}
              placeholder="e.g., TASK-001 (must be unique)"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block mb-2">Description</label>
            <RichDescriptionEditor
              value={formData.description}
              onChange={(nextDescription) =>
                setFormData((prev) => ({ ...prev, description: nextDescription }))
              }
              placeholder="Detailed description"
              rows={5}
              showPreview={true}
              suggestionContext={{
                title: formData.title || undefined,
                type: formData.type,
              }}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Status */}
            <div>
              <label className="block mb-2">Status *</label>
              <select
                aria-label="Task status"
                required
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as TaskStatus })}
                className={fieldClassName}
              >
                <option value="Active">Active</option>
                <option value="In Progress">In Progress</option>
                <option value="Code Complete">Code Complete</option>
                <option value="In Release">In Release</option>
                <option value="Completed">Completed</option>
                <option value="Blocked">Blocked</option>
                <option value="Duplicate">Duplicate</option>
                <option value="Other">Other</option>
                <option value="Deferred">Deferred</option>
                <option value="Not Required">Not Required</option>
              </select>
            </div>

            {/* Estimated Days */}
            <div>
              <label className="block mb-2">Estimated Days</label>
              <input
                aria-label="Estimated days"
                type="number"
                min="0"
                step="0.5"
                value={formData.estimatedDays}
                onChange={(e) => setFormData({ ...formData, estimatedDays: parseFloat(e.target.value) || 0 })}
                className={fieldClassName}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Epic */}
            {epics.length > 0 && (
              <div>
                <label className="block mb-2">Epic (Optional)</label>
                <select
                  aria-label="Epic"
                  value={formData.epicId}
                  onChange={(e) => setFormData({ ...formData, epicId: e.target.value })}
                  className={fieldClassName}
                >
                  <option value="">None</option>
                  {epics.map((epic) => (
                    <option key={epic.id} value={epic.id}>
                      {epic.title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Story */}
            {stories.length > 0 && (
              <div>
                <label className="block mb-2">Story (Optional)</label>
                <select
                  aria-label="Story"
                  value={formData.storyId}
                  onChange={(e) => setFormData({ ...formData, storyId: e.target.value })}
                  className={fieldClassName}
                >
                  <option value="">None</option>
                  {stories.map((story) => (
                    <option key={story.id} value={story.id}>
                      {story.title}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Assignees */}
          <div>
            <label className="block mb-2">Assignees</label>
            {assignableUsers.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">
                No project team members available for assignment.
              </p>
            ) : (
              <div className="space-y-2">
                {assignableUsers.map((user) => (
                  <label key={user.id} className="flex items-center gap-2 text-foreground/90">
                    <input
                      type="checkbox"
                      checked={assigneeIds.includes(user.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAssigneeIds([...assigneeIds, user.id]);
                        } else {
                          setAssigneeIds(assigneeIds.filter((id) => id !== user.id));
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <span>{user.name} ({user.email})</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Parent Tasks (Multiple parents support) */}
          {existingTasks.length > 0 && (
            renderSelectionChecklist(
              'Parent Tasks (Multiple Allowed)',
              existingTasks.map((task) => ({ id: task.id, label: task.title })),
              parentTaskIds,
              (optionId, checked) => {
                setParentTaskIds((previous) => toggleSelection(previous, optionId, checked));
              }
            )
          )}

          {project && (project.costs || []).length > 0 && (
            renderSelectionChecklist(
              'Linked Cost Items (Optional, multiple)',
              (project.costs || []).map((costItem) => ({
                id: costItem.id,
                label: `[${ensureCostItemCode(costItem)}] ${costItem.description} (${costItem.category}) - $${costItem.amount.toLocaleString()}`,
              })),
              costItemIds,
              (optionId, checked) => {
                setCostItemIds((previous) => toggleSelection(previous, optionId, checked));
              }
            )
          )}

          {project && (project.requirements || []).length > 0 && (
            renderSelectionChecklist(
              'Linked Requirements (Optional, multiple)',
              (project.requirements || []).map((requirement) => ({
                id: requirement.id,
                label: `[${requirement.code}] ${requirement.title}`,
              })),
              requirementIds,
              (optionId, checked) => {
                setRequirementIds((previous) => toggleSelection(previous, optionId, checked));
              }
            )
          )}

          {/* Bug-specific fields */}
          {formData.type === 'Bug' && (
            <>
              <div>
                <label className="block mb-2">Reported By (Customer Name/Email)</label>
                <input
                  type="text"
                  value={formData.reportedBy}
                  onChange={(e) => setFormData({ ...formData, reportedBy: e.target.value })}
                  className={fieldClassName}
                  placeholder="Customer name or email"
                />
              </div>

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.customerVisible}
                    onChange={(e) => setFormData({ ...formData, customerVisible: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span>Make status visible to customer</span>
                </label>
              </div>
            </>
          )}

          {/* External Links */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block">External Links (GitHub, Jira, etc.)</label>
              <button
                type="button"
                onClick={addExternalLink}
                className="flex items-center gap-1 text-sm text-primary hover:text-primary/80"
              >
                <Plus className="w-4 h-4" />
                Add Link
              </button>
            </div>
            {externalLinks.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">No external links yet</p>
            ) : (
              <div className="space-y-2">
                {externalLinks.map((link, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Type (e.g., GitHub, Jira)"
                      value={link.type}
                      onChange={(e) => updateExternalLink(index, 'type', e.target.value)}
                      className={`w-32 text-sm ${APP_NATIVE_FIELD_CLASS}`}
                    />
                    <input
                      type="url"
                      placeholder="URL"
                      value={link.url}
                      onChange={(e) => updateExternalLink(index, 'url', e.target.value)}
                      className={`flex-1 text-sm ${APP_NATIVE_FIELD_CLASS}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeExternalLink(index)}
                      aria-label="Remove external link"
                      title="Remove external link"
                      className="rounded-lg p-2 text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-border bg-background px-4 py-2 hover:bg-accent"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
            >
              Create Task
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
